"use client";

/**
 * PORTED — RO-11-04 / #44589.
 * Origin: `excellent/app/_chrome/dock-model-picker.tsx`.
 * Changes: next/link → RouterLink from @excellent/ui/router.
 */

import * as React from "react";
import { RouterLink as Link } from "@excellent/ui/router";
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@excellent/ui";
import { useApi, apiCall } from "@excellent/ui/api";
import { modelsConfig } from "@excellent/ui/api/contracts/models-config";
import { acpDock } from "@excellent/ui/api/contracts/acp-dock";

/**
 * Dock agent/model picker (docs/acp/PRD.md §5.4 M3 + models §14 M4).
 *
 * When ACP is enabled: **agent-first** select (claude / grok / …); model submenu
 * only for model-backed agents (harness) via Settings → Models.
 * When ACP is off: legacy provider+model branch toggle (byte-identical).
 *
 * The Select's content is portaled outside the dock root, so an open menu
 * blurs the root — `onOpenChange` lets DockBar hold the panel open while the
 * menu is up (the same override `historyOpen` provides).
 */
export function DockModelPicker({ onOpenChange }: { onOpenChange: (open: boolean) => void }) {
  const acp = useApi(acpDock.get, {});
  const models = useApi(modelsConfig.get, {});
  const [busy, setBusy] = React.useState(false);

  // Prefer ACP agent-first when the endpoint reports enabled.
  if (acp.data?.enabled) {
    return (
      <AcpAgentPicker
        data={acp.data}
        onOpenChange={onOpenChange}
        mutate={() => void acp.mutate()}
        modelsBranch={
          models.data?.copilot
            ? `${models.data.copilot.provider} · ${models.data.copilot.model}`
            : null
        }
      />
    );
  }

  // ── Legacy models.copilot branch picker (ACP off) ──────────────────────
  const copilot = models.data?.copilot ?? null;
  const branchEnabled = models.data?.copilotBranchEnabled ?? false;

  async function select(next: string) {
    setBusy(true);
    try {
      await apiCall(modelsConfig.setCopilot, { body: { enableBranch: next === "branch" } });
      await models.mutate();
    } finally {
      setBusy(false);
    }
  }

  if (!models.data || (!copilot && !branchEnabled)) return null;

  const value = branchEnabled && copilot ? "branch" : "claude";

  return (
    <span className="flex items-center gap-1">
      <Select value={value} onValueChange={(v) => void select(v)} onOpenChange={onOpenChange} disabled={busy}>
        <SelectTrigger size="sm" aria-label="Chat model">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="claude">Claude — default</SelectItem>
          {copilot && (
            <SelectItem value="branch">
              {copilot.provider} · {copilot.model}
            </SelectItem>
          )}
        </SelectContent>
      </Select>
      <Button asChild size="sm" variant="ghost">
        <Link href="/settings/agents/models">Configure…</Link>
      </Button>
    </span>
  );
}

function AcpAgentPicker({
  data,
  onOpenChange,
  mutate,
  modelsBranch,
}: {
  data: {
    selectedAgent: string;
    agents: Array<{ id: string; label: string; modelBacked: boolean }>;
  };
  onOpenChange: (open: boolean) => void;
  mutate: () => void;
  modelsBranch: string | null;
}) {
  const [busy, setBusy] = React.useState(false);
  const selected =
    data.agents.find((a) => a.id === data.selectedAgent)?.id ??
    data.agents[0]?.id ??
    "claude";
  const selectedMeta = data.agents.find((a) => a.id === selected);
  const modelBacked = selectedMeta?.modelBacked === true;

  async function selectAgent(agentId: string) {
    setBusy(true);
    try {
      await apiCall(acpDock.set, { body: { agentId } });
      mutate();
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="flex items-center gap-1">
      <Select
        value={selected}
        onValueChange={(v) => void selectAgent(v)}
        onOpenChange={onOpenChange}
        disabled={busy}
      >
        <SelectTrigger size="sm" aria-label="Chat agent">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {data.agents.map((a) => (
            <SelectItem key={a.id} value={a.id}>
              {a.label}
              {a.modelBacked ? " · model" : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {/* Model submenu only for harness / model-backed agents (PRD §5.4). */}
      {modelBacked && (
        <Button asChild size="sm" variant="ghost">
          <Link href="/settings/agents/models" title={modelsBranch ?? "Configure model"}>
            {modelsBranch ? "Model…" : "Model…"}
          </Link>
        </Button>
      )}
      {!modelBacked && (
        <Button asChild size="sm" variant="ghost">
          <Link href="/settings/agents/clis">Coding agents</Link>
        </Button>
      )}
    </span>
  );
}
